import { BrowserRouter, HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import PublicLayout from '@/layouts/PublicLayout';
import AppLayout from '@/layouts/AppLayout';
import Home from '@/pages/marketing/Home';
import ForMembers from '@/pages/marketing/ForMembers';
import ForCompanions from '@/pages/marketing/ForCompanions';
import Directory from '@/pages/browse/Directory';
import ServiceBrowse from '@/pages/browse/ServiceBrowse';
import Profile from '@/pages/account/Profile';
import Bookings from '@/pages/account/Bookings';
import Messages from '@/pages/account/Messages';
import CompanionServices from '@/pages/companion/Services';
import CompanionAvailability from '@/pages/companion/Availability';
import CompanionProfile from '@/pages/browse/CompanionProfile';
import Wallet from '@/pages/account/Wallet';
import CompanionDashboard from '@/pages/companion/Dashboard';
import CompanionProfileEditor from '@/pages/companion/ProfileEditor';
import Admin from '@/pages/admin/Dashboard';
import AdminSettings from '@/pages/admin/Settings';
import AdminServices from '@/pages/admin/Services';
import Status from '@/pages/Status';
import Terms from '@/pages/legal/Terms';
import Privacy from '@/pages/legal/Privacy';
import Refunds from '@/pages/legal/Refunds';
import Pricing from '@/pages/legal/Pricing';
import Contact from '@/pages/legal/Contact';
import Login from '@/pages/auth/Login';
import Signup from '@/pages/auth/Signup';
import CompanionLogin from '@/pages/auth/CompanionLogin';
import CompanionApply from '@/pages/auth/CompanionApply';
import RequireAuth from '@/components/common/RequireAuth';
import RequireBrowseFee from '@/components/common/RequireBrowseFee';
import { AuthProvider } from '@/lib/auth';
import { Button, Card } from '@/components/ui';

function NotFound() {
  return (
    <Card className="animate-rise mx-auto max-w-md px-6 py-16 text-center">
      <h1 className="text-2xl font-semibold">That page does not exist</h1>
      <p className="mt-2 text-(--ink-2)">The link may be old, or we may not have built it yet.</p>
      <div className="mt-6">
        <Button to="/">Back to home</Button>
      </div>
    </Card>
  );
}

/**
 * Hash routing is used only for the single-file static preview, which has no
 * server to rewrite paths. The real app uses history routing.
 */
const Router = import.meta.env.VITE_ROUTER === 'hash' ? HashRouter : BrowserRouter;

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Auth — full viewport, no site chrome */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/companion/login" element={<CompanionLogin />} />
          <Route path="/companion/apply" element={<CompanionApply />} />

          {/* Public — no wallet, no app nav */}
          <Route element={<PublicLayout />}>
            <Route index element={<Home />} />
            {/*<Route path="for-members" element={<ForMembers />} />*/}
            {/*<Route path="for-companions" element={<ForCompanions />} />*/}

            {/* Policy pages. Required live on the domain before a payment
              gateway will activate the merchant account. */}
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="refunds" element={<Refunds />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="contact" element={<Contact />} />

            <Route path="status" element={<Status />} />
            <Route path="services" element={<Navigate to="/#services" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Signed in. RequireAuth is a convenience — the real protection is
              RLS, which returns nothing without a valid session. */}
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            {/* The catalogue redirects to homepage anchor #services */}
            <Route path="services" element={<Navigate to="/#services" replace />} />
            <Route
              path="services/:slug"
              element={
                <RequireBrowseFee>
                  <ServiceBrowse />
                </RequireBrowseFee>
              }
            />
            <Route
              path="companions"
              element={
                <RequireBrowseFee>
                  <Directory />
                </RequireBrowseFee>
              }
            />
            <Route
              path="companions/:id"
              element={
                <RequireBrowseFee>
                  <CompanionProfile />
                </RequireBrowseFee>
              }
            />
            <Route path="bookings" element={<Bookings />} />
            <Route path="messages" element={<Messages />} />
            <Route path="profile" element={<Profile />} />
            <Route path="wallet" element={<Wallet />} />
          </Route>

          {/* Companion-only */}
          <Route
            element={
              <RequireAuth roles={['companion', 'admin']}>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route path="companion" element={<CompanionDashboard />} />
            <Route path="companion/profile" element={<CompanionProfileEditor />} />
            <Route path="companion/services" element={<CompanionServices />} />
            <Route path="companion/availability" element={<CompanionAvailability />} />
          </Route>

          {/* Admin-only. There is no admin signup — the role is granted by SQL. */}
          <Route
            element={
              <RequireAuth roles={['admin', 'moderator']}>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route path="admin" element={<Admin />} />
            <Route path="admin/settings" element={<AdminSettings />} />
            <Route path="admin/services" element={<AdminServices />} />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}
